import { useMemo } from "react"
import {
  Area,
  AreaChart as RechartsAreaChart,
  ResponsiveContainer,
  XAxis,
  ReferenceArea,
} from "recharts"

import { ChangeDataPointOutput } from "@costory/types/endpoints/billy"
import { ExploreResponses } from "@costory/types/endpoints/explorer"

import { getFormattedTrendData } from "@costory/shared/utils/charts"

type Props = {
  data: ExploreResponses.ExplorerData["totals"]
  series: { name: string; color: string }[]
  xAxisProps?: {
    ticks?: string[]
    tickFormatter?: (value: string) => string
    fontSize?: number
  }
  changeDataPointPeriods?: ChangeDataPointOutput["periods"]
  showTrends?: boolean
  currency?: string
  onClick?: () => void
}

export const AreaChartWithReference = ({
  data,
  series,
  xAxisProps,
  changeDataPointPeriods,
  showTrends = false,
  onClick,
}: Props) => {
  const referenceAreas = useMemo(() => {
    if (!showTrends || !changeDataPointPeriods) return []
    return getFormattedTrendData(changeDataPointPeriods) || []
  }, [showTrends, changeDataPointPeriods])

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsAreaChart
        data={data}
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
        margin={{ top: 5, right: 2, left: 2 }}
      >
        <XAxis
          dataKey="agg_date"
          tickLine={false}
          axisLine={false}
          ticks={xAxisProps?.ticks}
          tickFormatter={xAxisProps?.tickFormatter}
          fontSize={xAxisProps?.fontSize || 12}
          height={20}
          interval="preserveStartEnd"
          tick={{ fill: "var(--mantine-color-textPrimary-0)" }}
        />

        {referenceAreas.map(({ startDate, endDate, color }, i) => (
          <ReferenceArea
            key={`${startDate}-${endDate}-${i}`}
            x1={startDate}
            x2={endDate}
            fill={color}
            fillOpacity={0.15}
          />
        ))}

        {series.map(({ name, color }) => (
          <Area
            key={name}
            type="monotone"
            dataKey={name}
            stroke={color}
            fill={color}
            fillOpacity={0.15}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        ))}
      </RechartsAreaChart>
    </ResponsiveContainer>
  )
}
